import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Note } from '@app/_interfaces/note.interface';
import { ParseTextPipe } from '@app/_pipes/parse-text.pipe';
import { CacheService } from '@app/_services/cache.service';
import { MapService } from '@app/_services/map.service';
import { SettingsService } from '@app/_services/settings.service';
import { NoteStore } from '@app/_stores';
import { MeUserAvatarComponent } from '@app/components/common/account/me-user-avatar/me-user-avatar.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { NoteFormComponent } from '../note-form/note-form.component';
import { Attachment } from '@app/_interfaces/generic.interface';
import { LastUpdatedByService } from '@app/_services/last-updated-by.service';
import { CollaboraterRole } from '@app/_types/generic.type';
import { getRoleByUid, getCollaboratorCount } from '@app/_utils/members-model.utils';
import { ModalService } from '@app/_services/modal.service';
import { PublicIndicatorComponent } from '@app/components/shared/public-indicator/public-indicator.component';

@Component({
    selector: 'app-note-block',
    imports: [
        CommonModule,
        SvgComponent,
        ParseTextPipe,
        MeUserAvatarComponent,
        PublicIndicatorComponent
    ],
    templateUrl: './note-block.component.html',
    styleUrl: './note-block.component.scss'
})

export class NoteBlockComponent {
  readonly noteStore = inject(NoteStore);
  readonly lastUpdatedByService = inject(LastUpdatedByService);

  // Use signal-based inputs for proper reactivity
  noteId = input<string>('');
  show = input<string[]>([]);
  blockClass = input<string>('');
  descriptionType = input<string>('none');
  isLabel = input<boolean>(true);
  // Computed signal that properly reacts to noteId changes
  noteData: Signal<Note> = computed(() => {
    const id = this.noteId();
    return this.noteStore.idToNote()[id] || {} as Note;
  });

  moodMap: Signal<Record<number, string>> = this.mapService.moodMap;
  moodEmojiMap: Signal<Record<number, string>> = this.mapService.moodEmojiMap;
  roleMap: Signal<Record<string, string>> = this.mapService.collaboratorRoleMap;
  attachments: Signal<Attachment[]> = computed(() => {
    const note = this.noteData();
    if (!note.attachments) return [];
    return Object.values(note.attachments).filter(atch => atch.deletedAt === null);
  });

  role: Signal<CollaboraterRole> = computed(() => {
    if (this.noteData().uid === this.cc.user.uid) {
      return 'owner';
    } else if (this.noteData().members) {
      return getRoleByUid(this.noteData().members, this.cc.user.uid) || 'viewer';
    } else {
      return 'viewer';
    }
  });

  lastUpdatedAtText: Signal<string> = computed(() => {
    const note = this.noteData();
    return this.lastUpdatedByService.getLastUpdatedByString({
      lastUpdatedAt: note.localUpdatedAt,
      lastUpdatedByUid: note.lastUpdatedBy,
      membersModel: note.members,
      forMetaInfo: true,
    });
  });

  /** Whether the note has a lastUpdatedBy UID — controls whether the special icon is shown */
  hasLastUpdatedByUid: Signal<boolean> = computed(() => {
    return !!this.noteData().lastUpdatedBy;
  });

  /** Collaborator count from collaborators map (NOT memberUids which only has registered users) */
  collabCount: Signal<number> = computed(() => {
    return getCollaboratorCount(this.noteData()?.members);
  });

  constructor(
    public cc: CacheService,
    public mapService: MapService,
    public ss: SettingsService,
    public dialog: MatDialog,
    private modalService: ModalService
  ) {



  }

  openNoteForm() {
    const confirmDialog = this.modalService.openLevel1(
      NoteFormComponent,
      {
        mode: 'edit',
        value: this.noteData(),
      },
      { disableClose: true, autoFocus: false }
    );
    // return confirmDialog.afterClosed();
  }
}
